import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import { Box, Button, IconButton, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DateTime } from 'luxon'
import { useTranslate } from 'ra-core'
import { useState } from 'react'
import { DateField, ReferenceField, useRecordContext } from 'react-admin'

import { AssignOwnershipFormDialog, UpdateSeverityFormDialog } from './actions'
import { getIncidentCategoryIcon, IncidentCategoryField } from './IncidentCategorySelectInput'
import { getDamageReportStatusVisuals, IncidentReportStatusBadge } from './IncidentReportStatusUI'
import { getDamageOwnershipIcon, IncidentOwnershipField } from './OwnershipSelectInput'
import { SEVERITY_VISUALS, IncidentSeverityField, getIncidentReportSeverityIcon } from './SeveritySelectInput'
import { LIGHT_CARD_BORDER, SUMMARY_I18N_PREFIX } from './showShared'
import ReferenceValue from '../../components/ReferenceValue'
import {
  INCIDENT_REPORT_ACTION_ASSIGN_OWNERSHIP,
  INCIDENT_REPORT_ACTION_CONFIRM,
  INCIDENT_REPORT_ACTION_REJECT,
  INCIDENT_REPORT_ACTION_RESOLVE,
  INCIDENT_REPORT_ACTION_UPDATE_SEVERITY,
  INCIDENT_REPORT_SEVERITY_CRITICAL,
  INCIDENT_REPORT_STATUS_DISCARDED,
  INCIDENT_REPORT_STATUS_REPORTED,
  INCIDENT_REPORT_STATUS_RESOLVED,
} from '../../config/damages'
import { SYSTEM_PERMISSION_READ } from '../../config/permissions'
import { useResourcePermissions } from '../../domain/permissions'
import usersConfig from '../users/config'

const HEADER_I18N_PREFIX = 'resources.incident-reports.show.header'
const ATTENTION_BADGE_STYLES = {
  warning: {
    backgroundColor: '#fff7ed',
    color: '#9a3412',
  },
  error: {
    backgroundColor: '#fef2f2',
    color: '#b91c1c',
  },
}

const HeaderBadge = ({ icon: Icon, label, tone = 'warning' }) => {
  const styles = ATTENTION_BADGE_STYLES[tone]
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        borderRadius: 999,
        px: 1.25,
        py: 0.7,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.2,
        ...styles,
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
      <Box component="span">{label}</Box>
    </Box>
  )
}

const SummarySectionHeader = ({ title, isEditable = false, action = null }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '15px' }}>
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 12.5, letterSpacing: '0.01em', lineHeight: 1.2 }}
    >
      {title}
    </Typography>
    {isEditable && action}
  </Box>
)

const SummarySectionEditAction = ({ label, onClick }) => (
  <IconButton size="small" onClick={onClick} sx={{ color: 'text.secondary' }} aria-label={label} title={label}>
    <EditOutlinedIcon sx={{ fontSize: 18 }} />
  </IconButton>
)

const SummarySectionFrame = ({ children }) => (
  <Box
    sx={{
      minWidth: 0,
      px: { xs: 0, md: 2 },
      py: { xs: 0, md: 1.1 },
      pt: { xs: 2, md: 1.1 },
      minHeight: { xs: 'auto', sm: 136 },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}
  >
    {children}
  </Box>
)

const SummaryLeadingIcon = ({
  icon: Icon,
  color = 'text.secondary',
  backgroundColor = (theme) =>
    theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.6) : theme.palette.action.hover,
  borderColor = (theme) => alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.9 : 0.55),
  children = null,
}) => (
  <Box
    sx={{
      width: 50,
      height: 50,
      borderRadius: 999,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color,
      backgroundColor,
      boxShadow: `inset 0 0 0 1px ${borderColor}`,
    }}
  >
    {Icon ? <Icon sx={{ fontSize: 28 }} /> : children}
  </Box>
)

const useDamageReportAttention = (record, allowedActions) => {
  const translate = useTranslate()

  if (!record?.created_on) return null
  if ([INCIDENT_REPORT_STATUS_RESOLVED, INCIDENT_REPORT_STATUS_DISCARDED].includes(record.status)) return null

  const createdOn = DateTime.fromISO(record.created_on)
  if (!createdOn.isValid) return null

  const elapsedHours = Math.max(1, Math.floor(DateTime.now().diff(createdOn, 'hours').hours))
  const elapsedLabel =
    elapsedHours < 24
      ? translate(`${HEADER_I18N_PREFIX}.elapsedHours`, { smart_count: elapsedHours })
      : translate(`${HEADER_I18N_PREFIX}.elapsedDays`, { smart_count: Math.floor(elapsedHours / 24) })

  if (
    record.status === INCIDENT_REPORT_STATUS_REPORTED &&
    allowedActions?.includes('ASSIGN_OWNERSHIP') &&
    !record.ownership
  ) {
    return {
      icon: AssignmentIndOutlinedIcon,
      tone: 'warning',
      label: translate(`${HEADER_I18N_PREFIX}.attention.pendingAssignment`),
    }
  }

  if (record.severity === INCIDENT_REPORT_SEVERITY_CRITICAL) {
    return {
      icon: ReportProblemOutlinedIcon,
      tone: 'error',
      label: translate(`${HEADER_I18N_PREFIX}.attention.vehicleUnavailable`, { duration: elapsedLabel }),
    }
  }

  if (record.status === INCIDENT_REPORT_STATUS_REPORTED) {
    return {
      icon: ScheduleOutlinedIcon,
      tone: 'warning',
      label: translate(`${HEADER_I18N_PREFIX}.attention.unprocessed`, { duration: elapsedLabel }),
    }
  }

  return null
}

const IncidentReportIdentification = ({ allowedActions, sx = {} }) => {
  const record = useRecordContext()
  const translate = useTranslate()
  const attention = useDamageReportAttention(record, allowedActions)
  const [hasReadForUsers] = useResourcePermissions(usersConfig.name, SYSTEM_PERMISSION_READ)

  return (
    <Box sx={{ minWidth: 0, py: { xs: 0, md: 0.35 }, pr: { xs: 0, lg: 2.25 }, pb: 0, order: 1, ...sx }}>
      <Box sx={{ mb: 1.1 }}>
        <IncidentReportStatusBadge />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 0.8, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontSize: { xs: 32, md: 36 }, fontWeight: 700, lineHeight: 1 }}>
          {record.reference}
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.85,
          color: 'text.primary',
          '& svg': { fontSize: 19, flexShrink: 0, color: 'text.secondary' },
        }}
      >
        <DirectionsCarOutlinedIcon />
        <Typography variant="h6" sx={{ fontSize: 15, fontWeight: 600, color: 'text.primary', lineHeight: 1.25 }}>
          <ReferenceField reference="vehicles" source="vehicle_id" link={false} />
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 1.1,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          color: 'text.secondary',
          '& svg': { fontSize: 16, flexShrink: 0 },
        }}
      >
        <OutlinedFlagIcon />
        <Typography variant="body2" sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.45 }}>
          {translate(`${HEADER_I18N_PREFIX}.reportedOn`)}{' '}
          <DateField
            source="created_on"
            showTime
            options={{ year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }}
          />
          {hasReadForUsers && (
            <>
              {' '}
              {translate(`${HEADER_I18N_PREFIX}.reportedBy`)} <ReferenceValue reference="users" source="reporter_id" />
            </>
          )}
        </Typography>
      </Box>
      {attention && (
        <Box sx={{ mt: 1.5 }}>
          <HeaderBadge icon={attention.icon} label={attention.label} tone={attention.tone} />
        </Box>
      )}
    </Box>
  )
}

const CategorySummarySection = () => {
  const record = useRecordContext()
  const translate = useTranslate()
  const category = record.category
  const CategoryIcon = getIncidentCategoryIcon(category)

  return (
    <SummarySectionFrame>
      <SummarySectionHeader title={translate('resources.incident-reports.fields.category')} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25 }}>
        <SummaryLeadingIcon
          icon={category ? CategoryIcon : null}
          color="#475569"
          backgroundColor="#f8fafc"
          borderColor="rgba(148, 163, 184, 0.18)"
        />
        <Box sx={{ minWidth: 0 }}>
          <IncidentCategoryField
            value={category}
            icon={false}
            emptyLabel={translate(`${SUMMARY_I18N_PREFIX}.none`)}
            labelVariant="subtitle1"
            labelSx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.15 }}
          />
        </Box>
      </Box>
    </SummarySectionFrame>
  )
}

const SeveritySummarySection = ({ allowedActions }) => {
  const record = useRecordContext()
  const translate = useTranslate()
  const [isEditOpen, setEditOpen] = useState(false)
  const canEditSeverity = allowedActions.includes(INCIDENT_REPORT_ACTION_UPDATE_SEVERITY)
  const visual = SEVERITY_VISUALS[record.severity] || { color: '#64748b', backgroundColor: '#f8fafc' }
  const SeverityIcon = getIncidentReportSeverityIcon(record.severity)

  return (
    <>
      <SummarySectionFrame>
        <SummarySectionHeader
          title={translate('resources.incident-reports.fields.severity')}
          isEditable={canEditSeverity}
          action={
            canEditSeverity ? (
              <SummarySectionEditAction
                label={translate('resources.incident-reports.actions.update_severity.label')}
                onClick={() => setEditOpen(true)}
              />
            ) : null
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25 }}>
          <SummaryLeadingIcon backgroundColor="#f8fafc" borderColor="rgba(148, 163, 184, 0.18)">
            {record.severity ? <SeverityIcon sx={{ fontSize: 25, color: visual.color }} /> : null}
          </SummaryLeadingIcon>
          <Box sx={{ minWidth: 0 }}>
            <IncidentSeverityField
              value={record.severity}
              icon={false}
              emptyLabel={translate(`${SUMMARY_I18N_PREFIX}.none`)}
              labelVariant="subtitle1"
              labelSx={{
                fontSize: 18,
                fontWeight: 800,
                lineHeight: 1.15,
                color: record.severity ? visual.color : 'text.primary',
              }}
            />
          </Box>
        </Box>
      </SummarySectionFrame>
      {canEditSeverity ? <UpdateSeverityFormDialog open={isEditOpen} onClose={() => setEditOpen(false)} /> : null}
    </>
  )
}

const OwnershipSummarySection = ({ allowedActions }) => {
  const record = useRecordContext()
  const translate = useTranslate()
  const [isEditOpen, setEditOpen] = useState(false)
  const ownership = record.ownership
  const OwnershipIcon = getDamageOwnershipIcon(ownership)
  const canEditOwnership = allowedActions.includes(INCIDENT_REPORT_ACTION_ASSIGN_OWNERSHIP)

  return (
    <>
      <SummarySectionFrame>
        <SummarySectionHeader
          title={translate('resources.incident-reports.fields.ownership')}
          isEditable={canEditOwnership}
          action={
            canEditOwnership ? (
              <SummarySectionEditAction
                label={translate('resources.incident-reports.actions.assign_ownership.label')}
                onClick={() => setEditOpen(true)}
              />
            ) : null
          }
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.25 }}>
          <SummaryLeadingIcon
            icon={ownership ? OwnershipIcon : null}
            color="#475569"
            backgroundColor="#f8fafc"
            borderColor="rgba(148, 163, 184, 0.18)"
          />
          <Box sx={{ minWidth: 0 }}>
            <IncidentOwnershipField
              value={ownership}
              icon={false}
              emptyLabel={translate(`${SUMMARY_I18N_PREFIX}.unassigned`)}
              labelVariant="subtitle1"
              labelSx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.15 }}
            />
          </Box>
        </Box>
      </SummarySectionFrame>
      {canEditOwnership ? <AssignOwnershipFormDialog open={isEditOpen} onClose={() => setEditOpen(false)} /> : null}
    </>
  )
}

const HEADER_ACTION_BUTTON_SX = {
  minHeight: 38,
  px: 1.5,
  borderRadius: 2,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.2,
  textTransform: 'none',
  boxShadow: 'none',
  '& .MuiButton-startIcon': {
    mr: 0.75,
    '& svg': {
      fontSize: 18,
    },
  },
  '&:hover': {
    boxShadow: 'none',
  },
}

const IncidentReportHeaderActions = ({ allowedActions, onConfirm, onReject, onResolve }) => {
  const translate = useTranslate()
  const canConfirm = allowedActions.includes(INCIDENT_REPORT_ACTION_CONFIRM)
  const canReject = allowedActions.includes(INCIDENT_REPORT_ACTION_REJECT)
  const canResolve = allowedActions.includes(INCIDENT_REPORT_ACTION_RESOLVE)

  if (!canConfirm && !canReject && !canResolve) return null

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
      flexWrap="wrap"
      useFlexGap
    >
      {canReject ? (
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelOutlinedIcon />}
          onClick={onReject}
          sx={HEADER_ACTION_BUTTON_SX}
        >
          {translate('resources.incident-reports.actions.reject.confirmLabel')}
        </Button>
      ) : null}
      {canConfirm ? (
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={onConfirm}
          sx={HEADER_ACTION_BUTTON_SX}
        >
          {translate('resources.incident-reports.actions.confirm.confirmCta')}
        </Button>
      ) : null}
      {canResolve ? (
        <Button
          variant="contained"
          color="success"
          startIcon={<TaskAltRoundedIcon />}
          onClick={onResolve}
          sx={HEADER_ACTION_BUTTON_SX}
        >
          {translate('resources.incident-reports.actions.resolve.confirmLabel')}
        </Button>
      ) : null}
    </Stack>
  )
}

const IncidentReportSummaryCard = ({ allowedActions, onConfirm, onReject, onResolve }) => {
  const record = useRecordContext()
  const accentColor = getDamageReportStatusVisuals(record?.status)?.color || '#94a3b8'

  return (
    <Box
      sx={{
        position: 'relative',
        px: { xs: 2, md: 2.25 },
        py: { xs: 2, md: 2.25 },
        pl: { xs: 2.75, md: 3 },
        pr: { xs: 2.25, lg: 3 },
        borderRadius: 2.5,
        backgroundColor: 'background.paper',
        boxShadow: LIGHT_CARD_BORDER,
        width: '100%',
        boxSizing: 'border-box',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'auto auto auto' },
        alignItems: 'stretch',
        gap: { xs: 2.25, md: 8 },
        '&::before': {
          content: '\"\"',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: accentColor,
          borderTopLeftRadius: 20,
          borderBottomLeftRadius: 20,
        },
      }}
    >
      <IncidentReportIdentification allowedActions={allowedActions} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-start', lg: 'center' },
          alignItems: { xs: 'flex-start', lg: 'center' },
          minWidth: 0,
          order: { xs: 3, lg: 2 },
        }}
      >
        <IncidentReportHeaderActions
          allowedActions={allowedActions}
          onConfirm={onConfirm}
          onReject={onReject}
          onResolve={onResolve}
        />
      </Box>
      <Box
        sx={{
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: { xs: 1.5, md: 2 },
          pt: { xs: 0, lg: 0.25 },
          minHeight: { lg: 172 },
          order: { xs: 2, lg: 3 },
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, auto)' },
            columnGap: { xs: 0, md: 0.75 },
            rowGap: 2,
            alignItems: 'stretch',
          }}
        >
          <CategorySummarySection />
          <SeveritySummarySection allowedActions={allowedActions} />
          <OwnershipSummarySection allowedActions={allowedActions} />
        </Box>
      </Box>
    </Box>
  )
}

const IncidentReportShowHeader = ({ allowedActions, onConfirm, onReject, onResolve }) => (
  <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, minWidth: 0 }}>
    <IncidentReportSummaryCard
      allowedActions={allowedActions || []}
      onConfirm={onConfirm}
      onReject={onReject}
      onResolve={onResolve}
    />
  </Box>
)

export default IncidentReportShowHeader
